// src/pages/UserProfilePage.js
import React, { useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import useUserReservations from '../hooks/useUserReservations';
import useSubscription from '../hooks/useSubscription'; // <-- Import the new hook
import { Crown, CalendarDays, ArrowRight, Plus } from 'lucide-react';

import UserInfo from '../components/UserInfo';
import ActionButtons from '../components/ActionButtons';
import SubscriptionCard from '../components/SubscriptionCard';
import ReservationCard from '../components/ReservationCard';

import Config from "../Config.json";
import ICON from "../assets/icons/favicon.ico";

const TITLE = Config.SITE_TITLE + " - Página de Usuário";
const DESC = "Seu perfil na Deli da Persy.";
const CANONICAL = Config.SITE_DOMAIN + "/usuario";

function UserProfilePage() {
    const { user, loading } = useContext(AuthContext); 
    const navigate = useNavigate();

    // Hook API de Reservas
    const { reservations, loadingReservations, errorReservations } = useUserReservations(user);
    
    // 👇 NOVO: Hook API de Assinatura
    const { 
        subscription, 
        hasSubscription, 
        loadingSubscription, 
        errorSubscription 
    } = useSubscription(user);

    useEffect(() => {
        if (!loading) { 
            if (user === null) {
                navigate("/login");
            }
        }
    }, [user, loading, navigate]); 
    
    if (loading || user === null) { 
        return (
            <div className="min-h-screen flex flex-col items-center justify-center bg-[#FFF5EE]">
                <div className="w-12 h-12 border-4 border-[#dbcdac] border-t-[#528a89] rounded-full animate-spin mb-4"></div>
                <p className="text-[#528a89] font-serif text-lg animate-pulse">Carregando seu perfil...</p>
            </div>
        );
    }

    const userInfoData = {
        name: user.name, 
        email: user.email,
        picture: user.picture,
        phone: user.telefone
    };

    // Ordena as reservas para que as mais recentes (maior ID ou data de criação mais nova) fiquem no topo
    const sortedReservations = [...reservations].sort((a, b) => {
        if (a.createdAt && b.createdAt) {
            return new Date(b.createdAt) - new Date(a.createdAt);
        }
        return (b.id || 0) - (a.id || 0);
    });

    return (
        <div className="min-h-screen bg-[#FFF5EE] pt-24 pb-20 relative font-sans text-stone-800">
            <title>{TITLE}</title>
            <link rel="icon" href={ICON} />
            <link rel="canonical" href={CANONICAL} />
            <meta name="description" content={DESC} />
            
            <div className="absolute top-0 left-0 w-full h-full opacity-[0.03] pointer-events-none bg-[url('https://www.transparenttextures.com/patterns/aged-paper.png')]" />
            
            <div className="max-w-4xl mx-auto px-4 md:px-6 space-y-8 relative z-10 animate-in fade-in slide-in-from-bottom-4 duration-500">
                
                {/* --- 1. CABEÇALHO DO USUÁRIO --- */}
                {/* Pass the hasSubscription flag directly from the new hook */}
                <UserInfo user={userInfoData} hasSubscription={hasSubscription} /> 

                {/* --- 2. BOTÕES DE AÇÃO --- */}
                <ActionButtons />

                {/* --- 3. SEÇÃO DE ASSINATURA --- */}
                <section className="bg-white/60 backdrop-blur-sm p-6 md:p-8 rounded-[2rem] border border-[#dbcdac]/30 shadow-sm">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4 border-b border-[#dbcdac]/30 pb-4">
                        <h2 className="font-serif text-2xl text-[#971253] flex items-center gap-2 font-bold">
                            <Crown size={24} className="text-[#dbcdac]" /> Minha Assinatura
                        </h2>
                        {/* Only show "Ver Planos" button if completely loaded and user has no subscription */}
                        {(!loadingSubscription && !hasSubscription && !errorSubscription) && (
                            <button 
                                onClick={() => navigate('/sobre/assinatura')}
                                className="px-5 py-2.5 bg-[#971253] text-[#FFF5EE] text-sm font-bold uppercase tracking-widest rounded-xl hover:bg-[#7a0e42] transition-colors shadow-md w-full sm:w-auto text-center"
                            >
                                Ver Planos
                            </button>
                        )}
                    </div>

                    {/* API States Handling for Subscription */}
                    {loadingSubscription ? (
                        <div className="flex flex-col items-center justify-center py-10 text-center bg-[#FFF5EE]/50 border border-dashed border-[#dbcdac]/60 rounded-2xl">
                            <div className="w-8 h-8 border-4 border-[#dbcdac] border-t-[#971253] rounded-full animate-spin mb-3"></div>
                            <p className="text-stone-500 font-medium">Buscando sua assinatura...</p>
                        </div>
                        
                    ) : errorSubscription ? (
                        <div className="flex flex-col items-center justify-center py-8 text-center bg-red-50 border border-dashed border-red-200 rounded-2xl">
                            <p className="text-red-500 font-medium mb-2">{errorSubscription}</p>
                            <button onClick={() => window.location.reload()} className="text-sm font-bold text-[#971253] hover:underline">
                                Tentar novamente
                            </button>
                        </div>

                    ) : (
                        <SubscriptionCard subscription={subscription ? {
                            planName: subscription.plano || subscription.nomePlano,
                            startDate: subscription.data_inicio || subscription.dataInicio,
                            endDate: subscription.data_fim || subscription.dataFim,
                            active: subscription.ativo !== undefined ? subscription.ativo : subscription.ativa
                        } : null} />
                    )}
                </section>

                {/* --- 4. SEÇÃO DE RESERVAS --- */}
                <section className="bg-white/60 backdrop-blur-sm p-6 md:p-8 rounded-[2rem] border border-[#dbcdac]/30 shadow-sm">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4 border-b border-[#dbcdac]/30 pb-4">
                        <h2 className="font-serif text-2xl text-[#528a89] flex items-center gap-2 font-bold">
                            <CalendarDays size={24} className="text-[#dbcdac]" /> Minhas Reservas
                        </h2>
                        <button 
                            onClick={() => navigate('/minhas-reservas')} 
                            className="text-[#528a89] text-sm font-bold flex items-center gap-1.5 hover:text-[#3a6362] transition-colors w-full sm:w-auto justify-end"
                        >
                            Ver Histórico <ArrowRight size={16} />
                        </button>
                    </div>

                    <div className="space-y-4">
                        {loadingReservations ? (
                            <div className="flex flex-col items-center justify-center py-10 text-center bg-[#FFF5EE]/50 border border-dashed border-[#dbcdac]/60 rounded-2xl">
                                <div className="w-8 h-8 border-4 border-[#dbcdac] border-t-[#528a89] rounded-full animate-spin mb-3"></div>
                                <p className="text-stone-500 font-medium">Buscando suas reservas...</p>
                            </div>
                            
                        ) : errorReservations ? (
                            <div className="flex flex-col items-center justify-center py-8 text-center bg-red-50 border border-dashed border-red-200 rounded-2xl">
                                <p className="text-red-500 font-medium mb-2">{errorReservations}</p>
                                <button onClick={() => window.location.reload()} className="text-sm font-bold text-[#528a89] hover:underline">
                                    Tentar novamente
                                </button>
                            </div>

                        ) : sortedReservations.length > 0 ? (
                            <>
                                {sortedReservations.slice(0, 3).map((res, index) => (
                                    <div 
                                        key={res.id || index}
                                        onClick={() => navigate(`/reserva/${res.id}`)}
                                        className="cursor-pointer block group"
                                        title="Ver detalhes da reserva"
                                    >
                                        <ReservationCard reservation={res} />
                                    </div>
                                ))}
                                
                                <button 
                                    onClick={() => navigate('/reserva')}
                                    className="w-full mt-4 py-4 bg-white/40 border-2 border-dashed border-[#528a89]/40 text-[#528a89] text-sm font-bold uppercase tracking-widest rounded-2xl hover:bg-[#528a89] hover:text-[#FFF5EE] hover:border-[#528a89] transition-all duration-300 shadow-sm flex items-center justify-center gap-2 group"
                                >
                                    <Plus size={20} className="group-hover:rotate-90 transition-transform duration-300" /> 
                                    Nova Reserva
                                </button>
                            </>
                            
                        ) : (
                            <div className="flex flex-col items-center justify-center py-10 text-center bg-[#FFF5EE]/50 border border-dashed border-[#dbcdac]/60 rounded-2xl">
                                <CalendarDays size={48} className="text-[#dbcdac] mb-3 opacity-50" />
                                <p className="text-stone-500 mb-4">Você ainda não tem reservas futuras agendadas.</p>
                                <button 
                                    onClick={() => navigate('/reserva')}
                                    className="px-6 py-2.5 bg-white border border-[#528a89] text-[#528a89] text-sm font-bold uppercase tracking-widest rounded-xl hover:bg-[#528a89] hover:text-white transition-all shadow-sm"
                                >
                                    Fazer uma Reserva
                                </button>
                            </div>
                        )}
                    </div>
                </section>

            </div>
        </div>
    );
}

export default UserProfilePage;